"use client"

import { type ReactNode, useEffect, useRef } from "react"

type Props = {
  className: string
  labelledBy: string
  children: ReactNode
}

/**
 * Due segnali scritti sul DOM, senza stato (nessun re-render):
 * - `data-in`: la sezione entra in vista, le schede si rivelano;
 * - `data-flip`: la sezione è centrata nel viewport, la parola cambia.
 * Prima dell'idratazione e con motion ridotto resta visibile lo stato finale.
 */
export default function TryOnProductsReveal({ className, labelledBy, children }: Props) {
  const rootRef = useRef<HTMLElement>(null)
  const sentinelRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const node = rootRef.current

    if (!node) {
      return
    }

    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window)
    ) {
      node.setAttribute("data-in", "")
      node.setAttribute("data-flip", "")
      node.setAttribute("data-body", "")
      return
    }

    // Mobile: il corpo sta sotto la griglia, quindi ha un segnale suo (entra in
    // vista). Su desktop le righe aspettano `data-flip`, vedi il CSS.
    const bodyNode = node.querySelector("[data-body-trigger]")
    const body = bodyNode
      ? new IntersectionObserver(
          ([entry]) => {
            if (!entry?.isIntersecting) {
              return
            }
            node.setAttribute("data-body", "")
            body?.disconnect()
          },
          { rootMargin: "0px 0px -12% 0px", threshold: 0.2 }
        )
      : null
    if (bodyNode) {
      body?.observe(bodyNode)
    } else {
      node.setAttribute("data-body", "")
    }

    const enter = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }
        node.setAttribute("data-in", "")
        enter.disconnect()
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 }
    )

    // Desktop: il segnale è il centro della sezione (sentinella a metà altezza),
    // scatta quando attraversa il centro del viewport, cioè a modulo centrato.
    // Mobile: la sezione è alta e il titolo sta in cima, quindi il segnale è il
    // titolo stesso, quando sale oltre metà schermo.
    const isMobile = window.matchMedia("(max-width: 799px)").matches
    const flipTarget = isMobile
      ? node.querySelector("[data-flip-trigger]")
      : sentinelRef.current
    const center = flipTarget
      ? new IntersectionObserver(
          ([entry]) => {
            if (!entry) {
              return
            }
            // Se lo scroll salta la fascia, il bersaglio è già sopra di essa.
            const passed =
              entry.rootBounds !== null &&
              entry.boundingClientRect.top < entry.rootBounds.top
            if (!entry.isIntersecting && !passed) {
              return
            }
            node.setAttribute("data-flip", "")
            center?.disconnect()
          },
          {
            rootMargin: isMobile ? "0px 0px -50% 0px" : "-49% 0px -49% 0px",
            threshold: 0,
          }
        )
      : null

    node.setAttribute("data-ready", "")
    enter.observe(node)
    if (flipTarget) {
      center?.observe(flipTarget)
    }

    return () => {
      enter.disconnect()
      center?.disconnect()
      body?.disconnect()
    }
  }, [])

  return (
    <section
      aria-labelledby={labelledBy}
      className={className}
      data-testid="home-tryon-products"
      ref={rootRef}
    >
      <span
        aria-hidden="true"
        ref={sentinelRef}
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          width: 1,
          height: 1,
          pointerEvents: "none",
        }}
      />
      {children}
    </section>
  )
}
